import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { useI18n } from "../lib/i18n";
import { mediaUrl } from "../lib/api";

export function Lightbox({ images, index, onClose, title }) {
  const { t, isRtl } = useI18n();
  const [i, setI] = useState(index);
  useEffect(() => setI(index), [index]);
  const go = useCallback((d) => setI((x) => (x + d + images.length) % images.length), [images.length]);
  useEffect(() => {
    if (index === null) return undefined;
    const key = (e) => { if (e.key === "ArrowRight") go(isRtl ? -1 : 1); if (e.key === "ArrowLeft") go(isRtl ? 1 : -1); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [index, go, isRtl]);
  if (index === null || i === null) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gallery-lightbox" className="max-w-6xl border-cta/20 bg-canvas/95 p-2 sm:p-4 [&>button]:hidden">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="relative">
          <img src={mediaUrl(images[i])} alt={`${title} ${i + 1}`} className="max-h-[78vh] w-full rounded-sm object-contain" />
          <button type="button" data-testid="lightbox-prev" onClick={() => go(-1)} aria-label={t("common.prev")} className="absolute start-2 top-1/2 -translate-y-1/2 rounded-full bg-canvas/70 p-3 text-fg hover:text-cta"><ChevronLeft className="rtl-flip h-5 w-5" /></button>
          <button type="button" data-testid="lightbox-next" onClick={() => go(1)} aria-label={t("common.next")} className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full bg-canvas/70 p-3 text-fg hover:text-cta"><ChevronRight className="rtl-flip h-5 w-5" /></button>
          <button type="button" data-testid="lightbox-close" onClick={onClose} aria-label={t("common.close")} className="absolute end-2 top-2 rounded-full bg-canvas/70 p-2 text-fg hover:text-cta"><X className="h-5 w-5" /></button>
        </div>
        <p data-testid="lightbox-counter" className="text-center text-xs text-fg/60" dir="ltr">{i + 1} / {images.length}</p>
      </DialogContent>
    </Dialog>
  );
}

export default function Gallery({ images, title }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  if (!images?.length) return null;
  const [first, ...rest] = images;
  return (
    <div data-testid="property-gallery" className="grid gap-2 md:grid-cols-4 md:grid-rows-2">
      <button type="button" data-testid="gallery-image-0" onClick={() => setOpen(0)} className="group relative overflow-hidden rounded-sm md:col-span-3 md:row-span-2">
        <img src={mediaUrl(first)} alt={`${title} 1`} className="aspect-[16/10] h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
      </button>
      {rest.slice(0, 2).map((src, k) => (
        <button key={src + k} type="button" data-testid={`gallery-image-${k + 1}`} onClick={() => setOpen(k + 1)} className="group relative hidden overflow-hidden rounded-sm md:block">
          <img src={mediaUrl(src)} alt={`${title} ${k + 2}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          {k === 1 && images.length > 3 && <span className="absolute inset-0 flex items-center justify-center bg-canvas/60 text-sm text-fg">+{images.length - 3} {t("property.photos")}</span>}
        </button>
      ))}
      <Lightbox images={images} index={open} onClose={() => setOpen(null)} title={title} />
    </div>
  );
}
